import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { InfoCircle } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import JalaliDateTimeInput from "../../component/global/JalaliDateTimeInput";
import DBModal from "../../component/global/DBModal";
import DBSelect from "../../component/global/DBSelect";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SETTING_FORMS_USER_GROUP,
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_TREE,
    SURVEY_ITEM_TYPE_FORMS,
    SURVEY_LIST,
} from "../../helperData/ApiAddress";
import {
    systemUserGroupRequestParams,
    systemUserGroupSelectOptions,
} from "../../helperData/systemUserGroups";
import { flattenItemTypeOptions } from "../../pages/surveys/constants";
import { errorMessage, incentivesApi } from "./api";
import {
    ACTION_LABEL_KEYS,
    GROUP_ALL,
    ITEM_TYPE_ALL,
    SERVICE_SCOPED_ACTIONS,
    isAllItemTypes,
    calculateTierBudget,
    campaignErrorMessages,
    campaignPayload,
    campaignSchedulePayload,
    campaignToDraft,
    draftIssues,
    formatNumber,
    isRuleLocked,
} from "./helpers";

const ACTION_OPTIONS = Object.keys(ACTION_LABEL_KEYS);
const VERIFICATION_OPTIONS = ["automatic", "manual", "webhook"];

const FIELD_HELP_OVERLAY = {
    zIndex: 2000,
};
const FIELD_HELP_INNER = {
    maxWidth: 320,
    fontSize: 13,
    lineHeight: 1.6,
    textAlign: "start",
};

function FieldHelp({ text }) {
    if (!text) return null;
    return (
        <Tooltip
            title={text}
            trigger={["hover", "click"]}
            placement="top"
            overlayStyle={FIELD_HELP_OVERLAY}
            overlayInnerStyle={FIELD_HELP_INNER}
            getPopupContainer={() => document.body}
        >
            <button
                type="button"
                className="incentive-help-btn"
                aria-label={text}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                <InfoCircle size={16} aria-hidden="true" />
            </button>
        </Tooltip>
    );
}

const initialDraft = () => ({
    name: "",
    action: "feedback_submit",
    customAction: "",
    itemTypeId: null,
    itemId: null,
    formId: null,
    surveyId: null,
    audience: "all",
    groupIds: [],
    participantLimit: 100,
    perUserLimit: 1,
    verification: "automatic",
    webhookUrl: "",
    tiers: [{ threshold: 1, amount: 10 }],
    budget: 1000,
    startsAt: "",
    endsAt: "",
});

const toSelectOptions = (rows, labelKey = "name") =>
    (Array.isArray(rows) ? rows : rows?.results || []).map((row) => ({
        value: row.id,
        label: row[labelKey] || row.name || row.title,
    }));

const fieldError = (step, draft, t, options) => {
    const issue = draftIssues(draft, options).find((item) => item.step === step);
    return issue ? t(issue.key) : "";
};

const actionCaption = (draft, t) =>
    draft.action === "custom"
        ? draft.customAction
        : t(`incentives.actions.${ACTION_LABEL_KEYS[draft.action]}`);

function VerificationOption({
    value,
    checked,
    title,
    description,
    help,
    disabled,
    onChange,
}) {
    return (
        <label
            className={`incentive-verification-option${
                checked ? " is-selected" : ""
            }`}
        >
            <input
                type="radio"
                name="verification"
                value={value}
                checked={checked}
                disabled={disabled}
                onChange={() => !disabled && onChange(value)}
            />
            <span className="incentive-verification-copy">
                <span className="incentive-verification-title">
                    <strong>{title}</strong>
                    <FieldHelp text={help} />
                </span>
                <small>{description}</small>
            </span>
        </label>
    );
}

const applyPreset = (preset) => {
    const next = initialDraft();
    if (!preset) return next;
    return {
        ...next,
        action: preset.action || next.action,
        surveyId: preset.surveyId ?? next.surveyId,
        name: preset.name || next.name,
        tiers: preset.tiers || next.tiers,
        budget: preset.budget ?? next.budget,
        participantLimit:
            preset.participantLimit ?? next.participantLimit,
    };
};

export default function CampaignWizard({
    show,
    onHide,
    onPublished,
    campaignId,
    stacked,
    preset,
}) {
    const { t, i18n } = useTranslation();
    const [step, setStep] = useState(0);
    const [draft, setDraft] = useState(initialDraft);
    const [campaign, setCampaign] = useState(null);
    const [error, setError] = useState("");
    const [apiMessages, setApiMessages] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loadingCampaign, setLoadingCampaign] = useState(false);
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [forms, setForms] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const stepRefs = useRef([]);
    const nameInputRef = useRef(null);
    const blockersRef = useRef(null);
    const estimated = useMemo(
        () => calculateTierBudget(draft.tiers, draft.participantLimit),
        [draft.tiers, draft.participantLimit]
    );
    const groupOptions = useMemo(
        () => [
            { value: GROUP_ALL, label: t("incentives.audience.groupsAll") },
            ...userGroups,
        ],
        [t, userGroups]
    );
    const itemTypeSelectOptions = useMemo(
        () => [
            {
                value: ITEM_TYPE_ALL,
                label: t("incentives.fields.itemTypeAll"),
            },
            ...itemTypeOptions,
        ],
        [t, itemTypeOptions]
    );
    const hasConcreteItemType =
        Boolean(draft.itemTypeId) && !isAllItemTypes(draft.itemTypeId);
    const surveySelectOptions = useMemo(() => {
        if (
            !draft.surveyId ||
            surveys.some((row) => row.value === draft.surveyId)
        ) {
            return surveys;
        }
        return [
            {
                value: draft.surveyId,
                label: preset?.surveyTitle || String(draft.surveyId),
            },
            ...surveys,
        ];
    }, [surveys, draft.surveyId, preset?.surveyTitle]);

    const [getItemTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getItemTypeForms] = useRequest(SURVEY_ITEM_TYPE_FORMS, "GET");
    const [getSurveys] = useRequest(SURVEY_LIST, "GET");
    const [getUserGroups] = useRequest(SETTING_FORMS_USER_GROUP, "GET");

    const isEdit = Boolean(campaignId);
    const ruleLocked = isRuleLocked(campaign);
    const clientIssues = useMemo(
        () => draftIssues(draft, { ruleLocked, isEdit }),
        [draft, ruleLocked, isEdit]
    );
    const update = (patch) => {
        setApiMessages([]);
        setDraft((current) => ({ ...current, ...patch }));
    };
    const lockTarget = Boolean(preset?.lockTarget);
    const close = useCallback(() => {
        setStep(0);
        setDraft(applyPreset(preset));
        setCampaign(null);
        setError("");
        setApiMessages([]);
        setLoadingCampaign(false);
        onHide?.();
    }, [onHide, preset]);

    useEffect(() => {
        if (!show) return undefined;
        if (!campaignId) {
            setCampaign(null);
            setDraft(applyPreset(preset));
            setStep(0);
            setError("");
            setApiMessages([]);
            setLoadingCampaign(false);
            return undefined;
        }
        let cancelled = false;
        setLoadingCampaign(true);
        setError("");
        incentivesApi
            .campaign(campaignId)
            .then((data) => {
                if (cancelled) return;
                setCampaign(data);
                setDraft(campaignToDraft(data));
                setStep(0);
            })
            .catch((requestError) => {
                if (!cancelled) {
                    setError(
                        errorMessage(
                            requestError,
                            t("incentives.error.detail")
                        )
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingCampaign(false);
            });
        return () => {
            cancelled = true;
        };
    }, [show, campaignId, t, preset]);

    useEffect(() => {
        if (!show) return;
        getItemTypes()
            .then((tree) => setItemTypeOptions(flattenItemTypeOptions(tree)))
            .catch(() => setItemTypeOptions([]));
        getSurveys({ page_size: 100 })
            .then((rows) =>
                setSurveys(toSelectOptions(rows.results || rows, "title"))
            )
            .catch(() => setSurveys([]));
        getUserGroups(systemUserGroupRequestParams())
            .then((rows) => setUserGroups(systemUserGroupSelectOptions(rows)))
            .catch(() => setUserGroups([]));
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!show || !hasConcreteItemType) {
            setItems([]);
            setForms([]);
            return;
        }
        getItems({ item_type: draft.itemTypeId })
            .then((rows) => setItems(toSelectOptions(rows)))
            .catch(() => setItems([]));
        getItemTypeForms({ item_type: draft.itemTypeId })
            .then((rows) => setForms(toSelectOptions(rows)))
            .catch(() => setForms([]));
    }, [show, draft.itemTypeId, hasConcreteItemType]); // eslint-disable-line react-hooks/exhaustive-deps

    const goToStep = (next) => {
        const nextError =
            next > step
                ? fieldError(step, draft, t, { ruleLocked, isEdit })
                : "";
        if (nextError) {
            setError(nextError);
            return;
        }
        setError("");
        setApiMessages([]);
        setStep(next);
        requestAnimationFrame(() => stepRefs.current[next]?.focus());
    };
    const handleStepKeys = (event, index) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const rtl = i18n.dir() === "rtl";
        let next = index;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = 3;
        if (event.key === "ArrowRight") next += rtl ? -1 : 1;
        if (event.key === "ArrowLeft") next += rtl ? 1 : -1;
        next = Math.max(0, Math.min(3, next));
        if (next <= step) goToStep(next);
    };
    const handleActionChange = (action) => {
        update({
            action,
            itemTypeId: null,
            itemId: null,
            formId: null,
            surveyId: null,
        });
    };
    const handleItemTypeChange = (value) => {
        update({
            itemTypeId: value ?? null,
            itemId: null,
            formId: null,
        });
    };
    const handleGroupChange = (values) => {
        const next = values || [];
        if (next.includes(GROUP_ALL) && next[next.length - 1] === GROUP_ALL) {
            update({ groupIds: [GROUP_ALL] });
            return;
        }
        update({ groupIds: next.filter((value) => value !== GROUP_ALL) });
    };
    const addTier = () => {
        if (draft.tiers.length >= 3) return;
        update({
            tiers: [
                ...draft.tiers,
                {
                    threshold: draft.tiers.length + 1,
                    amount: draft.tiers[draft.tiers.length - 1].amount,
                },
            ],
        });
    };
    const setTier = (index, key, value) =>
        update({
            tiers: draft.tiers.map((tier, tierIndex) =>
                tierIndex === index ? { ...tier, [key]: value } : tier
            ),
        });
    const save = ({ publishAfter } = {}) => {
        const issues = draftIssues(draft, { ruleLocked, isEdit });
        if (issues.length) {
            setApiMessages([]);
            setError(issues.map((issue) => t(issue.key)).join(" "));
            requestAnimationFrame(() => blockersRef.current?.focus());
            return;
        }
        setSaving(true);
        setError("");
        setApiMessages([]);
        const payload = ruleLocked
            ? campaignSchedulePayload(draft)
            : campaignPayload(draft);
        const request = campaignId
            ? incentivesApi.updateCampaign(campaignId, payload)
            : incentivesApi.createCampaign(payload);
        request
            .then((saved) =>
                publishAfter
                    ? incentivesApi.publishCampaign(saved.id)
                    : saved
            )
            .then((saved) => {
                onPublished?.(saved);
                close();
            })
            .catch((requestError) => {
                const messages = campaignErrorMessages(
                    requestError,
                    t,
                    isEdit
                        ? "incentives.error.save"
                        : "incentives.error.publish"
                );
                setApiMessages(messages);
                setError(messages.join(" "));
            })
            .finally(() => setSaving(false));
    };

    const showServiceScope = SERVICE_SCOPED_ACTIONS.includes(draft.action);

    return (
        <DBModal
            show={show}
            onHide={close}
            minWidth={760}
            stacked={stacked}
            className="incentive-wizard"
            ariaLabelledby="incentive-wizard-title"
            initialFocusRef={nameInputRef}
        >
            <DBModal.Header
                closeBtn={close}
                title={
                    <span>
                        {t(
                            isEdit
                                ? "incentives.wizard.editTitle"
                                : "incentives.wizard.title"
                        )}
                    </span>
                }
            />
            <DBModal.Content>
                <div
                    className="incentive-steps"
                    role="tablist"
                    aria-label={t("incentives.wizard.steps")}
                >
                    {[0, 1, 2, 3].map((index) => (
                        <button
                            key={index}
                            ref={(node) => {
                                stepRefs.current[index] = node;
                            }}
                            type="button"
                            role="tab"
                            id={`incentive-step-${index}`}
                            aria-controls={`incentive-panel-${index}`}
                            aria-selected={step === index}
                            tabIndex={step === index ? 0 : -1}
                            disabled={index > step}
                            onClick={() => goToStep(index)}
                            onKeyDown={(event) => handleStepKeys(event, index)}
                        >
                            <span>{index + 1}</span>
                            {t(`incentives.wizard.step${index + 1}`)}
                        </button>
                    ))}
                </div>
                <section
                    id={`incentive-panel-${step}`}
                    role="tabpanel"
                    aria-labelledby={`incentive-step-${step}`}
                    className="incentive-wizard-panel"
                >
                    {loadingCampaign && (
                        <div
                            className="incentive-state"
                            role="status"
                            aria-live="polite"
                        >
                            <span
                                className="incentive-spinner"
                                aria-hidden="true"
                            />
                            {t("loading")}
                        </div>
                    )}
                    {ruleLocked && !loadingCampaign && (
                        <p className="incentive-locked-hint">
                            {t("incentives.wizard.lockedHint")}
                        </p>
                    )}
                    {!loadingCampaign && step === 0 && (
                        <div className="incentive-form-grid">
                            <DBFieldBlock
                                title={t("incentives.fields.name")}
                                required
                                span={2}
                            >
                                <DBInput
                                    size="large"
                                    inputRef={nameInputRef}
                                    value={draft.name}
                                    onChange={(event) =>
                                        update({ name: event.target.value })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("incentives.fields.action")}
                                hint={t("incentives.fields.actionHint")}
                                required
                            >
                                <DBSelect
                                    size="large"
                                    value={draft.action}
                                    disabled={ruleLocked || lockTarget}
                                    onChange={handleActionChange}
                                    options={ACTION_OPTIONS.map((value) => ({
                                        value,
                                        label: t(
                                            `incentives.actions.${ACTION_LABEL_KEYS[value]}`
                                        ),
                                    }))}
                                />
                                {(draft.action === "introduction" ||
                                    draft.action === "share") && (
                                    <p className="incentive-field-hint">
                                        {t(
                                            `incentives.actions.${draft.action}Hint`
                                        )}
                                    </p>
                                )}
                            </DBFieldBlock>
                            {draft.action === "custom" && (
                                <DBFieldBlock
                                    title={t("incentives.fields.customAction")}
                                    hint={t("incentives.fields.customActionHint")}
                                    required
                                >
                                    <DBInput
                                        size="large"
                                        value={draft.customAction}
                                        disabled={ruleLocked}
                                        onChange={(event) =>
                                            update({
                                                customAction: event.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                            )}
                            {draft.action === "survey_submit" && (
                                <DBFieldBlock
                                    title={t("incentives.fields.survey")}
                                    hint={t("incentives.fields.surveyHint")}
                                    required
                                >
                                    <DBSelect
                                        size="large"
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder={t("form.select")}
                                        value={draft.surveyId ?? undefined}
                                        disabled={ruleLocked || lockTarget}
                                        onChange={(value) =>
                                            update({
                                                surveyId: value ?? null,
                                            })
                                        }
                                        options={surveySelectOptions}
                                    />
                                </DBFieldBlock>
                            )}
                            {showServiceScope && (
                                <>
                                    <DBFieldBlock
                                        title={t("incentives.fields.itemType")}
                                        hint={t("incentives.fields.itemTypeHint")}
                                        required
                                    >
                                        <DBSelect
                                            size="large"
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder={t("form.select")}
                                            value={draft.itemTypeId ?? undefined}
                                            disabled={ruleLocked}
                                            onChange={handleItemTypeChange}
                                            options={itemTypeSelectOptions}
                                        />
                                        {isAllItemTypes(draft.itemTypeId) && (
                                            <p className="incentive-field-hint">
                                                {t(
                                                    "incentives.fields.itemTypeAllHint"
                                                )}
                                            </p>
                                        )}
                                    </DBFieldBlock>
                                    <DBFieldBlock
                                        title={t("incentives.fields.item")}
                                        hint={t("incentives.fields.itemHint")}
                                    >
                                        <DBSelect
                                            size="large"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            placeholder={t("form.select")}
                                            value={draft.itemId ?? undefined}
                                            onChange={(value) =>
                                                update({
                                                    itemId: value ?? null,
                                                })
                                            }
                                            options={items}
                                            disabled={
                                                ruleLocked || !hasConcreteItemType
                                            }
                                        />
                                    </DBFieldBlock>
                                    <DBFieldBlock
                                        title={t("incentives.fields.form")}
                                        hint={t("incentives.fields.formHint")}
                                    >
                                        <DBSelect
                                            size="large"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            placeholder={t("form.select")}
                                            value={draft.formId ?? undefined}
                                            onChange={(value) =>
                                                update({
                                                    formId: value ?? null,
                                                })
                                            }
                                            options={forms}
                                            disabled={
                                                ruleLocked || !hasConcreteItemType
                                            }
                                        />
                                    </DBFieldBlock>
                                </>
                            )}
                        </div>
                    )}
                    {!loadingCampaign && step === 1 && (
                        <div className="incentive-form-grid">
                            <DBFieldBlock
                                title={t("incentives.fields.audience")}
                                hint={t("incentives.fields.audienceHint")}
                            >
                                <DBSelect
                                    size="large"
                                    value={draft.audience}
                                    disabled={ruleLocked}
                                    onChange={(value) =>
                                        update({
                                            audience: value,
                                            groupIds:
                                                value === "group"
                                                    ? draft.groupIds
                                                    : [],
                                        })
                                    }
                                    options={[
                                        {
                                            value: "all",
                                            label: t("incentives.audience.all"),
                                        },
                                        {
                                            value: "group",
                                            label: t(
                                                "incentives.audience.group"
                                            ),
                                        },
                                    ]}
                                />
                            </DBFieldBlock>
                            {draft.audience === "group" && (
                                <DBFieldBlock
                                    title={t("incentives.fields.groupIds")}
                                    required
                                    hint={t("incentives.fields.groupIdsHint")}
                                >
                                    <DBSelect
                                        size="large"
                                        mode="multiple"
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        maxTagCount={2}
                                        placeholder={t("form.select")}
                                        value={draft.groupIds}
                                        disabled={ruleLocked}
                                        onChange={handleGroupChange}
                                        options={groupOptions}
                                    />
                                </DBFieldBlock>
                            )}
                            <DBFieldBlock
                                title={t("incentives.fields.participantLimit")}
                                hint={t(
                                    "incentives.fields.participantLimitHint"
                                )}
                            >
                                <DBInput
                                    type="number"
                                    size="large"
                                    min="1"
                                    value={draft.participantLimit}
                                    disabled={ruleLocked}
                                    onChange={(event) =>
                                        update({
                                            participantLimit: event.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("incentives.fields.perUserLimit")}
                                hint={t("incentives.fields.perUserLimitHint")}
                            >
                                <DBInput
                                    type="number"
                                    size="large"
                                    min="1"
                                    value={draft.perUserLimit}
                                    disabled={ruleLocked}
                                    onChange={(event) =>
                                        update({
                                            perUserLimit: event.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <fieldset className="wide incentive-verification">
                                <legend>
                                    {t("incentives.fields.verification")}
                                </legend>
                                <div className="incentive-verification-list">
                                    {VERIFICATION_OPTIONS.map((value) => (
                                        <VerificationOption
                                            key={value}
                                            value={value}
                                            checked={
                                                draft.verification === value
                                            }
                                            title={t(
                                                `incentives.verification.${value}`
                                            )}
                                            description={t(
                                                `incentives.verification.${value}Description`
                                            )}
                                            help={t(
                                                `incentives.verification.${value}Help`
                                            )}
                                            onChange={(next) =>
                                                update({ verification: next })
                                            }
                                            disabled={ruleLocked}
                                        />
                                    ))}
                                </div>
                            </fieldset>
                            {draft.verification === "webhook" && (
                                <DBFieldBlock
                                    title={t("incentives.fields.webhook")}
                                    hint={t("incentives.fields.webhookHint")}
                                    span={2}
                                >
                                    <DBInput
                                        type="url"
                                        size="large"
                                        dir="ltr"
                                        value={draft.webhookUrl}
                                        disabled={ruleLocked}
                                        onChange={(event) =>
                                            update({
                                                webhookUrl: event.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                            )}
                        </div>
                    )}
                    {!loadingCampaign && step === 2 && (
                        <>
                            <div className="incentive-tier-list">
                                {draft.tiers.map((tier, index) => (
                                    <div key={index}>
                                        <strong>
                                            {t("incentives.tier", {
                                                number: index + 1,
                                            })}
                                        </strong>
                                        <label>
                                            <span>
                                                {t(
                                                    "incentives.fields.threshold"
                                                )}
                                                <FieldHelp
                                                    text={t(
                                                        "incentives.fields.thresholdHint"
                                                    )}
                                                />
                                            </span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={tier.threshold}
                                                disabled={ruleLocked}
                                                onChange={(e) =>
                                                    setTier(
                                                        index,
                                                        "threshold",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </label>
                                        <label>
                                            <span>
                                                {t("incentives.fields.coins")}
                                                <FieldHelp
                                                    text={t(
                                                        "incentives.fields.coinsHint"
                                                    )}
                                                />
                                            </span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={tier.amount}
                                                disabled={ruleLocked}
                                                onChange={(e) =>
                                                    setTier(
                                                        index,
                                                        "amount",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </label>
                                        {draft.tiers.length > 1 && !ruleLocked && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    update({
                                                        tiers: draft.tiers.filter(
                                                            (_, itemIndex) =>
                                                                itemIndex !==
                                                                index
                                                        ),
                                                    })
                                                }
                                            >
                                                {t("incentives.removeTier")}
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {!ruleLocked && (
                                    <button
                                        type="button"
                                        className="incentive-secondary-action"
                                        onClick={addTier}
                                        disabled={draft.tiers.length >= 3}
                                    >
                                        {t("incentives.addTier")}
                                    </button>
                                )}
                            </div>
                            <div className="incentive-form-grid">
                                <DBFieldBlock
                                    title={t("incentives.fields.budget")}
                                    hint={t("incentives.fields.budgetHint")}
                                >
                                    <DBInput
                                        type="number"
                                        size="large"
                                        min="1"
                                        value={draft.budget}
                                        disabled={ruleLocked}
                                        onChange={(event) =>
                                            update({
                                                budget: event.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                                <div
                                    className="incentive-estimate"
                                    aria-live="polite"
                                >
                                    <span className="incentive-estimate-label">
                                        {t("incentives.fields.maxExposure")}
                                        <FieldHelp
                                            text={t(
                                                "incentives.fields.maxExposureHint"
                                            )}
                                        />
                                    </span>
                                    <strong>
                                        {formatNumber(
                                            estimated,
                                            i18n.language === "fa"
                                                ? "fa-IR"
                                                : "en-US"
                                        )}{" "}
                                        {t("incentives.coins")}
                                    </strong>
                                </div>
                                <DBFieldBlock
                                    title={t("incentives.fields.starts")}
                                    hint={t("incentives.fields.startsHint")}
                                >
                                    {i18n.language?.startsWith("fa") ? (
                                        <JalaliDateTimeInput
                                            value={draft.startsAt}
                                            onChange={(value) =>
                                                update({ startsAt: value })
                                            }
                                        />
                                    ) : (
                                        <DBInput
                                            type="datetime-local"
                                            size="large"
                                            value={draft.startsAt}
                                            onChange={(event) =>
                                                update({
                                                    startsAt:
                                                        event.target.value,
                                                })
                                            }
                                        />
                                    )}
                                </DBFieldBlock>
                                <DBFieldBlock
                                    title={t("incentives.fields.ends")}
                                    hint={t("incentives.fields.endsHint")}
                                >
                                    {i18n.language?.startsWith("fa") ? (
                                        <JalaliDateTimeInput
                                            value={draft.endsAt}
                                            onChange={(value) =>
                                                update({ endsAt: value })
                                            }
                                        />
                                    ) : (
                                        <DBInput
                                            type="datetime-local"
                                            size="large"
                                            value={draft.endsAt}
                                            onChange={(event) =>
                                                update({
                                                    endsAt: event.target.value,
                                                })
                                            }
                                        />
                                    )}
                                </DBFieldBlock>
                            </div>
                        </>
                    )}
                    {!loadingCampaign && step === 3 && (
                        <div className="incentive-review">
                            <dl>
                                <div>
                                    <dt>{t("incentives.fields.name")}</dt>
                                    <dd>
                                        {draft.name.trim() ||
                                            t(
                                                "incentives.validation.missingValue"
                                            )}
                                    </dd>
                                </div>
                                <div>
                                    <dt>{t("incentives.fields.action")}</dt>
                                    <dd>
                                        {actionCaption(draft, t) ||
                                            t(
                                                "incentives.validation.missingValue"
                                            )}
                                    </dd>
                                </div>
                                <div>
                                    <dt>{t("incentives.fields.audience")}</dt>
                                    <dd>
                                        {t(
                                            `incentives.audience.${draft.audience}`
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt>{t("incentives.fields.budget")}</dt>
                                    <dd>
                                        {formatNumber(draft.budget)}{" "}
                                        {t("incentives.coins")}
                                    </dd>
                                </div>
                            </dl>
                            <div className="incentive-rule-preview">
                                <strong>{t("incentives.rulePreview")}</strong>
                                <p>
                                    {t("incentives.ruleSentence", {
                                        amount: draft.tiers[0].amount,
                                        action: actionCaption(draft, t),
                                        limit: draft.perUserLimit,
                                    })}
                                </p>
                            </div>
                            {(clientIssues.length > 0 ||
                                apiMessages.length > 0) && (
                                <div
                                    ref={blockersRef}
                                    className="incentive-publish-blockers"
                                    role="alert"
                                    tabIndex={-1}
                                >
                                    <strong>
                                        {t(
                                            "incentives.validation.cannotPublish"
                                        )}
                                    </strong>
                                    <ul>
                                        {clientIssues.map((issue) => (
                                            <li key={issue.field}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        goToStep(issue.step)
                                                    }
                                                >
                                                    {t(
                                                        `incentives.wizard.step${
                                                            issue.step + 1
                                                        }`
                                                    )}
                                                    {": "}
                                                    {t(issue.key)}
                                                </button>
                                            </li>
                                        ))}
                                        {apiMessages.map((message, index) => (
                                            <li key={`api-${index}`}>
                                                {message}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    {error && step !== 3 && (
                        <p className="incentive-error" role="alert">
                            {error}
                        </p>
                    )}
                </section>
            </DBModal.Content>
            <DBModal.Footer>
                {step > 0 && (
                    <DBButton
                        type="secondary"
                        caption={t("incentives.back")}
                        onClick={() => goToStep(step - 1)}
                    />
                )}
                {step < 3 ? (
                    <DBButton
                        caption={t("incentives.next")}
                        disabled={loadingCampaign}
                        onClick={() => goToStep(step + 1)}
                    />
                ) : (
                    <>
                        {isEdit && campaign?.status === "draft" && (
                            <DBButton
                                type="secondary"
                                caption={t("incentives.save")}
                                loading={saving}
                                disabled={loadingCampaign}
                                onClick={() => save()}
                            />
                        )}
                        <DBButton
                            caption={
                                isEdit && campaign?.status !== "draft"
                                    ? t("incentives.save")
                                    : t("incentives.publish")
                            }
                            loading={saving}
                            disabled={loadingCampaign}
                            onClick={() =>
                                save({
                                    publishAfter:
                                        !isEdit ||
                                        campaign?.status === "draft",
                                })
                            }
                        />
                    </>
                )}
            </DBModal.Footer>
        </DBModal>
    );
}
